// External libraries
import type { ReactElement } from 'react';
import React, { useEffect, useState } from 'react'; // eslint-disable-line no-unused-vars
import type { IncentiveOfferState } from '@/store';
import { useStoreState } from '@/store';
// Internal modules
import { features, getGlobals } from '@/lib';
import { useMountLogging } from '@/lib/reactHooks';
import {
  CheckoutIncentiveV1,
  CheckoutIncentiveV2,
  CheckoutIncentiveV3,
  SuccessV1,
  SuccessV2,
  SuccessV3,
} from './Components';
import { updateCheckoutIncentive, updateIncentiveOfferState } from './handlers';
import {
  logExperimentationDetails,
  logIncentiveConfirmationShown,
  logIncentiveShown,
} from './fpti';

type UIState = {
  countToggles: number;
  incentiveOfferState: IncentiveOfferState;
};

type FormState = {
  initialOnboardOption: string;
  onboardOption: string;
};

const CheckoutIncentives = (): ReactElement => {
  useMountLogging('CheckoutIncentives');

  // Offer is first shown when user manually turns toggle off.
  const [offerEligible, setOfferEligible] = useState<boolean>(false);

  // Experiment to show UI variant1, variant 2, or variant 3
  const showV1 = features.isActive('shouldShowSignupIncentiveDelayedVariant1');
  const showV2 = features.isActive('shouldShowSignupIncentiveDelayedVariant2');
  const showV3 = features.isActive('shouldShowSignupIncentiveDelayedVariant3');

  const { onboardOption, initialOnboardOption } = useStoreState(
    ({ form }): FormState => {
      return {
        initialOnboardOption: form.initialOnboardOption?.value,
        onboardOption: form.onboardOption?.value,
      };
    },
  );

  const { countToggles, incentiveOfferState } = useStoreState(
    ({ ui }): UIState => {
      return {
        countToggles: ui.countToggles,
        incentiveOfferState: ui.incentiveOfferState,
      };
    },
  );

  // Extract currency symbol from selected country
  const { localeMetadata } = getGlobals().initialGqlData;
  const currencySymbol = localeMetadata?.currencySymbol;

  /**
   * This method is to check if we should user is eligible for offer to be shown
   * signup toggle should be true/on initially
   * should be in EC token flow
   * Backend flag isSignupIncentiveEligible should be true
   */
  const checkOfferEligibility = (onboardOption: string): boolean => {
    // Offer eligibility criteria: should be EC Token
    const isECToken = !features.isActive('isWps');
    const isSignupIncentiveEligible = features.isActive(
      'isSignupIncentiveEligible',
    );
    const isControl = features.isActive(
      'shouldShowSignupIncentiveDelayedControl',
    );
    const isEligible =
      onboardOption === 'signup' &&
      isSignupIncentiveEligible &&
      isECToken &&
      !isControl;
    const ineligibilityReason: Array<string> = [];

    if (onboardOption !== 'signup') {
      ineligibilityReason.push('NOT_SIGNUP_FLOW');
    }
    if (!isECToken) {
      ineligibilityReason.push('NOT_EC_FLOW');
    }
    if (!isSignupIncentiveEligible) {
      ineligibilityReason.push('SERVER_FOUND_INELIGIBLE');
    }
    if (isControl) {
      ineligibilityReason.push('USER_UNDER_CONTROL_GROUP');
    }

    logExperimentationDetails(isEligible, ineligibilityReason);
    return isEligible;
  };

  // setInitialOnboardOption
  useEffect(() => {
    setOfferEligible(checkOfferEligibility(initialOnboardOption));
  }, []);

  // Kepp track of manual user toggles
  useEffect(() => {
    // Instrumentation requirement
    if (offerEligible && onboardOption === 'guest') {
      logIncentiveShown();
    }
    updateIncentiveOfferState(
      incentiveOfferState,
      offerEligible,
      onboardOption,
      countToggles,
    );
  }, [onboardOption, offerEligible]);

  // Set incentive eligiblity in form state
  useEffect(() => {
    if (incentiveOfferState.shouldDisplayOfferAccepted) {
      // Instrumentation requirement
      logIncentiveConfirmationShown();
    }
    updateCheckoutIncentive(
      incentiveOfferState.shouldDisplayOfferAccepted as boolean,
    );
  }, [incentiveOfferState.shouldDisplayOfferAccepted]);

  return (
    <>
      {/* Variant 1 */}
      {incentiveOfferState.shouldDisplayOffer && showV1 && (
        <CheckoutIncentiveV1 currencySymbol={currencySymbol} />
      )}
      {incentiveOfferState.shouldDisplayOfferAccepted && showV1 && (
        <SuccessV1 currencySymbol={currencySymbol} />
      )}

      {/* Variant 2 */}
      {incentiveOfferState.shouldDisplayOffer && showV2 && (
        <CheckoutIncentiveV2 currencySymbol={currencySymbol} />
      )}
      {incentiveOfferState.shouldDisplayOfferAccepted && showV2 && (
        <SuccessV2 currencySymbol={currencySymbol} />
      )}

      {/* Variant 3 */}
      {incentiveOfferState.shouldDisplayOffer && showV3 && (
        <CheckoutIncentiveV3 currencySymbol={currencySymbol} />
      )}
      {incentiveOfferState.shouldDisplayOfferAccepted && showV3 && (
        <SuccessV3 currencySymbol={currencySymbol} />
      )}
    </>
  );
};

export { CheckoutIncentives };
